import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type HttpError } from 'http-errors';

import type {
  IntegrationData,
  SendToIntegrationContainerData,
  Template
} from '../types';
import { sendIssue, type SendIssueBody } from '../utils/clients/walnutApi';
import { useSendToIntegrationModalSteps } from './useSendToIntegrationModalSteps';
import { type SendIssueEventArgs } from './useSendToIntegrationLogic';
import {
  prepareUIFieldsForSubmission,
  type UIFields
} from '../utils/prepareSendIssueFields';

type useSendToIntegrationModalProps = {
  selectedIntegrationData: IntegrationData | null;
  sendToIntegrationContainerData: SendToIntegrationContainerData;
  apiHostname: string;
  authFn: () => Promise<string>;
  userEnterpriseId: string;
  closeModal: () => void;
  onSuccess: (args: SendIssueEventArgs) => void;
  onError?: (args: SendIssueEventArgs) => void;
};

export const useSendToIntegrationModal = ({
  sendToIntegrationContainerData,
  selectedIntegrationData,
  apiHostname,
  authFn,
  userEnterpriseId,
  closeModal,
  onSuccess,
  onError
}: useSendToIntegrationModalProps) => {
  const { t } = useTranslation();
  const [isSaving, setIsSaving] = useState(false);
  const [sendIssueError, setSendIssueError] = useState<string>();
  const [selectedTemplate, setSelectedTemplate] = useState<Template>();
  const [uiFields, setUIFields] = useState<UIFields>();

  const {
    steps,
    isProjectsLoading,
    projectsByConnections,
    projectsError,
    connectionIdsWithLoadingErrors
  } = useSendToIntegrationModalSteps({
    selectedIntegrationData,
    sendToIntegrationContainerData,
    apiHostname,
    authFn,
    userEnterpriseId,
    selectedTemplate,
    setSelectedTemplate,
    uiFields,
    setUIFields
  });

  const onModalSubmit = async () => {
    if (
      !selectedIntegrationData ||
      !uiFields ||
      isSaving ||
      !selectedTemplate
    ) {
      return;
    }

    const fieldData = prepareUIFieldsForSubmission(uiFields);

    const body: SendIssueBody = {
      template_id: selectedTemplate.templateId,
      group_id: sendToIntegrationContainerData.issues[0].groupId || '',
      product_source_slug: sendToIntegrationContainerData.hostProduct.slug,
      issues: sendToIntegrationContainerData.issues,
      mappedIntegrationFields: Object.entries(fieldData).map(
        ([key, value]) => ({ key, value })
      )
    };

    const sendIssueData: SendIssueEventArgs = {
      integrationProductSlug: selectedIntegrationData.productSlug,
      project: selectedTemplate.project_id,
      issueType: selectedTemplate.issue_type_id,
      template: selectedTemplate.templateId,
      isMultipleShare: selectedIntegrationData.isMultipleIssues
    };

    try {
      setIsSaving(true);
      const token = await authFn();

      await sendIssue({
        apiHostname: apiHostname,
        token,
        enterpriseId: userEnterpriseId,
        connectionId: selectedTemplate.connection.id,
        body
      });

      setIsSaving(false);
      onSuccess(sendIssueData);
      closeModal();
    } catch (error) {
      const e = error as HttpError;
      const { productName, isMultipleIssues } = selectedIntegrationData;

      const errorMessage = isMultipleIssues
        ? t('Error with sending all issues to {{ productName }}.', {
            productName
          })
        : t('Error with sending issue to {{ productName }}.', {
            productName
          });

      const { errorFields, errorId } = e?.details || {};

      const errorFieldsMessage = errorFields?.length
        ? t(' The following fields are invalid: {{ errorFields }}.', {
            errorFields: errorFields.join(', ')
          })
        : '';

      const errorIdMessage = errorId
        ? t(' Error ID: {{errorId}}.', { errorId })
        : '';

      setSendIssueError(
        `${errorMessage}${errorFieldsMessage}${errorIdMessage}`
      );

      setIsSaving(false);
      onError?.(sendIssueData);
    }
  };

  return {
    steps,
    onModalSubmit,
    isProjectsLoading,
    projectsByConnections,
    projectsError,
    isSaving,
    sendIssueError,
    setSendIssueError,
    connectionIdsWithLoadingErrors
  };
};
